// ─── Feuille : ajouter aux COURSES ────────────────────────────────
// Une modale, deux façons de dire « combien » :
//   • mode « personnes » (recette, repas) — un Stepper recalcule les quantités ;
//   • mode « quantité » (ingrédient seul) — « pour combien de personnes »
//     n'aurait aucun sens, on saisit directement la quantité dans son unité.
// Dans les deux cas la liste des ingrédients est AFFICHÉE et MODIFIABLE avant
// d'ajouter : c'est le dernier moment où l'on peut corriger, une fois dans la
// liste de courses les quantités sont fusionnées avec l'existant.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, Stepper, stylesForm, useUI, useT } from '@mop/core';
import { pasInitial, pasSuivant } from './services/pas';

export default function FeuilleAjoutCourses({
  visible, titre, emoji = '🛒', mode = 'personnes',
  personnesInit = 4, ingredientInit = null,
  calculer, emojiPour, onValider, onFermer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [personnes, setPersonnes] = useState(personnesInit);
  const [lignes, setLignes] = useState([]);
  const [pas, setPas] = useState(1);

  // À l'ouverture : on part du nombre de personnes de l'élément, et on calcule.
  useEffect(() => {
    if (!visible) return;
    if (mode === 'quantite') {
      const i = ingredientInit || {};
      const l = [{ nom: i.nom || '', unite: i.unite || '', quantite: '' }];
      setLignes(l);
      setPas(pasInitial(l));
      return;
    }
    const n = Math.max(1, Number(personnesInit) || 1);
    setPersonnes(n);
    const l = calculer ? calculer(n) : [];
    setLignes(l);
    setPas(pasInitial(l));
  }, [visible, mode, personnesInit, ingredientInit]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Changer le nombre de personnes REMPLACE les quantités : on repart du calcul,
  // sinon une correction manuelle faite avant serait silencieusement conservée
  // sur une base qui n'a plus cours.
  const changerPersonnes = (n) => {
    const v = Math.max(1, n);
    setPersonnes(v);
    if (calculer) setLignes(calculer(v));
  };

  const setQuantite = (i, q) => setLignes(l => l.map((x, k) => (k === i ? { ...x, quantite: q } : x)));
  const retirer = (i) => setLignes(l => l.filter((_, k) => k !== i));

  // Le pas est celui affiché en haut à droite (1 / 10 / 100), commun à toutes
  // les lignes : une recette se corrige rarement en grammes ici et en pièces là.
  const ajuster = (i, sens) => setLignes(l => l.map((x, k) => {
    if (k !== i) return x;
    const n = parseFloat(String(x.quantite ?? '').replace(',', '.'));
    // Quantité libre (« au goût ») : le premier appui repart de zéro plutôt
    // que d'effacer un texte que l'utilisateur a peut-être voulu garder.
    const base = Number.isFinite(n) ? n : 0;
    const v = Math.max(0, Math.round((base + sens * pas) * 100) / 100);
    return { ...x, quantite: String(v) };
  }));

  // On n'ajoute que ce qui a un nom ; une quantité vide reste permise (« au
  // goût », « selon besoin » — le champ est libre côté recette).
  const valider = () => {
    const utiles = lignes.filter(x => (x.nom || '').trim());
    if (utiles.length) onValider?.(utiles);
    onFermer?.();
  };

  const rien = lignes.filter(x => (x.nom || '').trim()).length === 0;

  return (
    <Feuille visible={visible} titre={titre || t('courses.ajouterALaListe')} emoji={emoji}
      hauteur={0.8} onFermer={onFermer}
      action={{ icone: '🛒', label: t('courses.ajouterALaListe'), disabled: rien, onPress: valider }}>

      {mode === 'personnes' && (
        <View style={s.bloc}>
          <Text style={s.blocTitre}>{t('courses.pourCombien')}</Text>
          <Stepper valeur={personnes}
            onMoins={() => changerPersonnes(personnes - 1)}
            onPlus={() => changerPersonnes(personnes + 1)} />
        </View>
      )}

      {/* Le pas vit à côté du titre, pas dans chaque ligne : un seul réglage,
          visible, et les lignes restent lisibles sur un écran étroit. */}
      <View style={s.sectLigne}>
        <Text style={s.sect}>{t('repas.ingredients')}</Text>
        <TouchableOpacity style={s.pasBtn} onPress={() => setPas(pasSuivant)}
          accessibilityLabel={`${t('courses.pas')} ${pas}`}>
          <Text style={s.pasBtnTxt}>± {pas}</Text>
        </TouchableOpacity>
      </View>
      {lignes.length === 0 ? (
        <Text style={s.vide}>—</Text>
      ) : lignes.map((x, i) => (
        <View key={`${x.nom}-${i}`} style={s.ligne}>
          <Text style={{ fontSize: 17 }}>{emojiPour ? emojiPour(x.nom) : '🛒'}</Text>
          <Text style={s.nom} numberOfLines={1}>{x.nom}</Text>
          {/* − / + encadrent le champ : la saisie clavier reste possible (utile
              pour « 250 »), les boutons servent aux ajustements rapides. */}
          <TouchableOpacity style={s.pas} onPress={() => ajuster(i, -1)} accessibilityLabel="−">
            <Text style={s.pasTxt}>−</Text>
          </TouchableOpacity>
          <TextInput style={s.qte} value={String(x.quantite ?? '')}
            onChangeText={v => setQuantite(i, v)}
            keyboardType="decimal-pad" placeholder="—" placeholderTextColor={UI.faint} />
          <TouchableOpacity style={s.pas} onPress={() => ajuster(i, 1)} accessibilityLabel="＋">
            <Text style={s.pasTxt}>＋</Text>
          </TouchableOpacity>
          <Text style={s.unite} numberOfLines={1}>{x.unite || ''}</Text>
          {mode === 'personnes' && lignes.length > 1 && (
            <TouchableOpacity onPress={() => retirer(i)} hitSlop={{ top: 8, bottom: 8, left: 6, right: 6 }}>
              <Text style={{ fontSize: 15, color: UI.muted }}>⊝</Text>
            </TouchableOpacity>
          )}
        </View>
      ))}
      <Text style={s.aide}>{t('courses.ajusterAide')} {t('courses.pasAide')}</Text>
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  bloc: { backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, padding: 13, marginBottom: 6 },
  blocTitre: { fontSize: 13.5, fontWeight: '700', color: UI.ink, marginBottom: 10 },
  sectLigne: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, marginBottom: 8 },
  sect: { fontSize: 12, fontWeight: '800', color: UI.accent, textTransform: 'uppercase', letterSpacing: 0.5 },
  pasBtn: { paddingVertical: 5, paddingHorizontal: 11, borderRadius: 999, backgroundColor: UI.bg, borderWidth: 0.5, borderColor: UI.border },
  pasBtnTxt: { fontSize: 12.5, fontWeight: '800', color: UI.accent, fontVariant: ['tabular-nums'] },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 8, paddingHorizontal: 11, marginBottom: 7 },
  nom: { flex: 1, minWidth: 0, fontSize: 14, color: UI.ink, marginRight: 2 },
  // Cible tactile confortable sans écraser le nom de l'ingrédient.
  pas: { width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: UI.bg, borderWidth: 0.5, borderColor: UI.border },
  pasTxt: { fontSize: 16, fontWeight: '800', color: UI.accent, lineHeight: 19 },
  qte: { width: 52, textAlign: 'center', fontSize: 14.5, fontWeight: '700', color: UI.ink, backgroundColor: UI.bg, borderRadius: 8, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 6, paddingHorizontal: 4 },
  unite: { minWidth: 30, fontSize: 12, color: UI.muted },
  vide: { fontSize: 13, color: UI.faint },
  aide: { fontSize: 12, color: UI.faint, lineHeight: 17, marginTop: 8 },
});
